import { routerHistory } from "../app"
import { LANGUAGES, COUNTRIES } from "../../shared/constants"
import { Select, AutoComplete } from "antd"
import React, { useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { SearchMagnifier, Cross } from "../../shared/icons"
import { Header } from "../../shared/components/header"
import { Footer } from "../../shared/components/footer"
import { Grid } from "../grid"
import { useParams } from "react-router-dom"
import { getCookie, setCookie, deleteCookie } from "../../shared/helpers/cookie-helper"
import { getLogo } from "../../shared/helpers/logo"
import { login } from "../../shared/helpers/login"
import { localize } from "../../shared/utils/localize"
import styles from "./main.module.css"

const { Option } = Select

export const Main = () => {
	const { hash } = useParams()
	const DEFAULT_FILTERS = { from: 0, size: 25, hash, materialName: "" }
	const [gridData, setGridData] = useState([])
	const [loading, setLoading] = useState(false)
	const [mainBlockLoading, setMainBlockLoading] = useState(true)
	const [showClearButton, setShowClearButton] = useState(false)
	const [searchValue, setSearchValue] = useState("")
	const [filters, setFilters] = useState(DEFAULT_FILTERS)
	const [showData, setShowData] = useState(false)
	const [vendors, setVendors] = useState([])
	const [searchVendorName, setSearchVendorName] = useState("")
	const [autocompleteTimeOut, setAutocompleteTimeOut] = useState(null)
	const [materials, setMaterials] = useState([])
	const [autocompleteTimeOutMaterial, setAutocompleteTimeOutMaterial] = useState(null)

	const showOwn = () => {
		setShowData(true)
		filterChange(DEFAULT_FILTERS)
	}

	const clearFilters = () => {
		setFilters(DEFAULT_FILTERS)
	}

	const checkLogin = async () => {
		if (getCookie("OutbackHash") !== hash) {
			const res = await login(hash)
			if (res.status === 200) {
				const { domain } = await res.json()
				setCookie("OutbackDomain", domain, 86400)
				setCookie("OutbackHash", hash, 86400)
				await getLogo(domain, hash)
				setMainBlockLoading(false)
				return showOwn()
			} else {
				deleteCookie("OutbackDomain")
				deleteCookie("OutbackHash")
				deleteCookie("Logo")
				routerHistory.push("/login")
				return false
			}
		} else {
			setMainBlockLoading(false)
			return showOwn()
		}
	}

	useEffect(() => { checkLogin() }, [])

	useEffect(() => {
		setShowClearButton(!!searchValue)
	  }, [searchValue]);

	const getData = async () => {
		const response = await fetch(`${process.env.REACT_APP_OUTBACK_API_URL}/${process.env.REACT_APP_STAGE}/search`, {
			method: "POST",
			headers: { "Content-Type": "application/json", "OutbackHash": hash },
			body: JSON.stringify(filters)
		})
		const data = await response.json()
		const currentPage = filters.from / filters.size + 1
		setGridData({ data: data.items, totalResults: data.totalResults, currentPage })
		setLoading(false)
	}

	useEffect(() => {
		if (showData) {
			setLoading(true)
			getData()
		}
	}, [filters])

	const filterChange = (filter) => {
		if (!showData && (filters.materialName || filter.materialName)) {
			setShowData(true)
		}
		setFilters({ ...filters, ...filter })
	}

	const search = () => {
		if (searchValue) {
			filterChange({ materialName: searchValue, from: 0 })
		}
	}

	const clear = () => {
		setSearchValue("")
		showOwn()
	}

	const materialAutocomplete = async (value) => {
		if (!value) {
		  setMaterials([]);
		  return;
		}

		try {
			const response = await fetch(`${process.env.REACT_APP_OUTBACK_API_URL}/${process.env.REACT_APP_STAGE}/autocompleteName`, {
				method: "POST",
				headers: { "Content-Type": "application/json", "OutbackHash": hash },
				body: JSON.stringify({ name: value })
			})
			const data = await response.json();
			setMaterials(data.map(item => ({ value: item })));
		  } catch (err) {
			console.error("Material autocomplete error:", err);
			setMaterials([]);
		  }
		}
		
		const searchMaterialChange = (value) => {
		  setSearchValue(value);
		
		  if (autocompleteTimeOutMaterial != null) {
			clearTimeout(autocompleteTimeOutMaterial);
		  }
		
		  if (value.length) {
			const timeOut = setTimeout(() => {
			  materialAutocomplete(value);
			}, 350);
			setAutocompleteTimeOutMaterial(timeOut);
		  } else {
			filterChange({ materialName: value, from: 0 });
			setMaterials([]);
		  }
		}

	const vendorAutocomplete = async (value) => {
		const response = await fetch(`${process.env.REACT_APP_OUTBACK_API_URL}/${process.env.REACT_APP_STAGE}/autocomplete`, {
			method: "POST",
			headers: { "Content-Type": "application/json", "OutbackHash": hash },
			body: JSON.stringify({ vendorName: value, languages: filters.languages, countries: filters.countries })
		})
		const data = await response.json()
		setVendors(data.map(item => ({ value: item })))
	}

	const searchVendorNameChange = (value) => {
		setSearchVendorName(value)

		if (value.length) {
			if (!filters.vendorName || filters.vendorName !== value) {
				if (autocompleteTimeOut != null) {
					clearTimeout(autocompleteTimeOut)
				}

				const timeOut = setTimeout(() => {
					vendorAutocomplete(value)
				}, 350)
				setAutocompleteTimeOut(timeOut)
			}
		} else {
			filterChange({ vendorName: value })
		}
	}

	return (
		mainBlockLoading ?
			<div
				className={`${mainBlockLoading ? "custom-loader-spinner-type mainspace" : "mainspace"}`}
				role="status"
				aria-live="polite"
				aria-label="Loading"
			/>
			:
			<div className="maincontainer" role="main">
				<Header clearFilters={clearFilters}/>
				<div className={styles.search_block}>
					<div className={styles.search_field}>
						<div className={`${styles.search_field_inner} ${showClearButton ? styles.search_process : ""}`}>
							<AutoComplete
  								options={materials}
  								value={searchValue}
  								onChange={searchMaterialChange}
  								onSelect={(value) => filterChange({ materialName: value, from: 0 })}
placeholder={localize("Material Name or CAS Number or Part Number")}
								aria-label={localize("Material Name or CAS Number or Part Number")}
  								onKeyDown={({ key }) => {
									if (key === "Enter" && searchValue) {
										filterChange({ materialName: searchValue, from: 0 });
									}
								}}
							/>
							<button type="button" onClick={search} aria-label={localize("Search")}>
								<SearchMagnifier />
							</button>
							{showClearButton && (
								<button
									className={styles.clear_btn}
									type="button"
									onClick={clear}
									aria-label={localize("Clear")}
								>
									<Cross />
								</button>
							)}
						</div>
						<button type="button" aria-label={localize("Show-Own")} className={styles.show_own_link} onClick={showOwn}>
							<FormattedMessage id="Show-Own" />
						</button>
					</div>
				</div>
				<div className={styles.country_language_selectors}>
					<div className={styles.country_language_selectors_inner}>
						<Select
							mode="multiple"
placeholder={localize("Select Countries")}
							aria-label={localize("Select Countries")}
							onChange={(e) => filterChange({ countries: e, from: 0 })}
							value={filters.countries}
							optionLabelProp="label"
							filterOption={
								(inputValue, { label }) => label.toLowerCase().includes(inputValue.toLowerCase())
							}
						>
							{COUNTRIES.map(conuntry => (
								<Option
									value={conuntry.code}
									label={conuntry.name}
									key={conuntry.code}
								>
									{conuntry.name}
								</Option>
							))}
						</Select>
						<Select
							mode="multiple"
placeholder={localize("Select Languages")}
							aria-label={localize("Select Languages")}
							onChange={(e) => filterChange({ languages: e, from: 0 })}
							value={filters.languages}
							optionLabelProp="label"
							filterOption={
								(inputValue, { label }) => label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(inputValue.toLowerCase())
							}
						>
							{LANGUAGES.map(language => (
								<Option
									value={language.code}
									label={language.name}
									key={language.code}
								>
									{language.name}
								</Option>
							))}
						</Select>
						<AutoComplete
							className="vendor-autocomplete"
							options={vendors}
							value={searchVendorName}
							onChange={searchVendorNameChange}
							onSelect={(value) => filterChange({ vendorName: value, from: 0 })}
placeholder={localize("Vendor Name")}
							aria-label={localize("Vendor Name")}
							onKeyDown={({ key }) => {
								if (key === "Enter" && searchVendorName) {
									filterChange({ vendorName: searchVendorName })
								}
							}}
						/>
					</div>
				</div>
				<div className={`outback-main-grid ${loading ? "custom-loader-spinner-type" : ""}`}>
					<Grid data={gridData} onChange={filterChange} setLoading={setLoading} />
				</div>
				<Footer />
			</div>
	)
}